import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "./LiveDonations.css";
import axios from "axios";

const LiveDonations = ({ streamId, streamerName, onDonationSent }) => {
  const { user } = useSelector((state) => state.user);
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [recentDonations, setRecentDonations] = useState([]);
  const [topDonators, setTopDonators] = useState([]);

  const quickAmounts = [5, 10, 25, 50, 100, 250, 500, 1000];

  useEffect(() => {
    fetchRecentDonations();
    fetchTopDonators();
    const interval = setInterval(fetchRecentDonations, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const fetchRecentDonations = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/live/donations/${streamId}/recent`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setRecentDonations(data.donations);
    } catch (error) {
      console.error("Error fetching donations:", error);
    }
  };

  const fetchTopDonators = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/live/donations/${streamId}/top`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setTopDonators(data.topDonators);
    } catch (error) {
      console.error("Error fetching top donators:", error);
    }
  };

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
  };

  const openConfirmation = () => {
    if (!amount || parseFloat(amount) < 1) {
      alert("O valor mínimo de doação é R$ 1,00");
      return;
    }
    setShowConfirm(true);
  };

  const sendDonation = async () => {
    setIsSending(true);

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/live/donations/${streamId}`,
        {
          amount: parseFloat(amount),
          message: message.trim(),
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (onDonationSent) onDonationSent(data);
      
      // Resetar formulário
      setAmount("");
      setMessage("");
      setShowConfirm(false);
      fetchRecentDonations();
      fetchTopDonators();

      // Mostrar notificação de sucesso
      alert(`Doação de R$ ${amount} enviada com sucesso! 🎉`);
    } catch (error) {
      console.error("Error sending donation:", error);
      alert("Erro ao enviar doação. Verifique seu saldo e tente novamente.");
    } finally {
      setIsSending(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  return (
    <div className="live-donations">
      <div className="donations-header">
        <h3>
          <svg viewBox="0 0 20 20">
            <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
          </svg>
          Apoiar {streamerName}
        </h3>
      </div>

      <div className="donations-content">
        <div className="donation-form">
          <label>Valor da Doação</label>
          <div className="amount-input-wrapper">
            <span className="currency-symbol">R$</span>
            <input
              type="number"
              className="amount-input"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="1"
              step="0.01"
            />
          </div>

          <div className="quick-amounts">
            {quickAmounts.map((value) => (
              <button
                key={value}
                className={`quick-amount ${amount === value.toString() ? "active" : ""}`}
                onClick={() => handleQuickAmount(value)}
              >
                R$ {value}
              </button>
            ))}
          </div>

          <label>Mensagem (opcional)</label>
          <textarea
            className="message-input"
            placeholder="Escreva uma mensagem de apoio..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={200}
            rows={3}
          />
          <span className="char-count">{message.length}/200</span>

          <button
            className="donate-button"
            onClick={openConfirmation}
            disabled={!amount || parseFloat(amount) < 1}
          >
            <svg viewBox="0 0 20 20">
              <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
            </svg>
            Doar {amount ? formatCurrency(parseFloat(amount)) : ""}
          </button>
        </div>

        <div className="recent-donations">
          <h4>Doações Recentes</h4>
          <div className="donations-list">
            {recentDonations.length === 0 ? (
              <div className="empty-state">
                <svg viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
                <p>Seja o primeiro a doar!</p>
              </div>
            ) : (
              recentDonations.map((donation, index) => (
                <div key={index} className="donation-item">
                  <div className="donation-avatar">
                    <img
                      src={donation.user.picture}
                      alt={donation.user.first_name}
                    />
                  </div>
                  <div className="donation-info">
                    <div className="donation-header-info">
                      <span className="donor-name">{donation.user.first_name}</span>
                      <span className="donation-amount">
                        {formatCurrency(donation.amount)}
                      </span>
                    </div>
                    {donation.message && (
                      <p className="donation-message">{donation.message}</p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="top-donators">
          <h4>
            <svg viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            Top Doadores
          </h4>
          <div className="top-list">
            {topDonators.length === 0 ? (
              <div className="empty-state">
                <p>Nenhuma doação ainda</p>
              </div>
            ) : (
              topDonators.map((donator, index) => (
                <div key={index} className="top-item">
                  <span className={`rank rank-${index + 1}`}>#{index + 1}</span>
                  <img
                    src={donator.user.picture}
                    alt={donator.user.first_name}
                  />
                  <span className="top-name">{donator.user.first_name}</span>
                  <span className="top-amount">
                    {formatCurrency(donator.totalAmount)}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showConfirm && (
        <div className="donation-confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="donation-confirm" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar Doação</h3>
            <div className="confirm-details">
              <p>Você está prestes a doar:</p>
              <div className="confirm-amount">{formatCurrency(parseFloat(amount))}</div>
              <p>Para: <strong>{streamerName}</strong></p>
              {message && (
                <div className="confirm-message">
                  <p>Mensagem:</p>
                  <blockquote>{message}</blockquote>
                </div>
              )}
            </div>
            <div className="confirm-buttons">
              <button
                className="confirm-cancel"
                onClick={() => setShowConfirm(false)}
                disabled={isSending}
              >
                Cancelar
              </button>
              <button
                className="confirm-send"
                onClick={sendDonation}
                disabled={isSending}
              >
                {isSending ? (
                  <>
                    <div className="spinner"></div>
                    Processando...
                  </>
                ) : (
                  "Confirmar Doação"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveDonations;
